/**
 * 学业页 —— 决策④定的钩子：让 Carson 一眼看到自己离目标还有多远。
 *
 * 两条克制：
 * 1. **量纲不明就不画进度条。** 托福那两个 4.5 不在 0–120 上，量纲没确认之前
 *    画任何一条都是错的，所以只显示分数本身。
 * 2. **占位科目不假装有数据**，而是明确请他来填 —— 这既是决策③，
 *    也是他第一次打开这个 App 时唯一有事可做的地方。
 */

import { useCallback, useEffect, useState } from 'react'
import { Button, EmptyState } from '@pwaship/ui'
import { api } from '../lib/api'

interface Subject {
  id: number
  kind: string
  name: string
  placeholder: boolean
  targetScore: string | null
  examDate: string | null
  daysToExam: number | null
  scale: { min: number; max: number; step: number } | null
  latestScore: { score: string; official: boolean; takenAt: string | null } | null
  ratio: number | null
  targetRatio: number | null
  logCount: number
  lastLogAt: string | null
}

const KIND_ORDER = ['AP', 'SAT', 'TOEFL', 'IELTS']
const KIND_LABEL: Record<string, string> = { AP: 'AP', SAT: 'SAT', TOEFL: '托福', IELTS: '雅思' }

export default function Academics({ role }: { role: 'carson' | 'neo' }) {
  const [subjects, setSubjects] = useState<Subject[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)

  const load = useCallback(async () => {
    const r = await api.get<{ subjects: Subject[] }>('/api/academics/dashboard')
    if (r.ok) { setSubjects(r.data.subjects); setError(null) }
    else setError(r.message)
  }, [])

  useEffect(() => { void load() }, [load])

  if (error) return <EmptyState title="读不到学业数据" description={error} />
  if (!subjects) return <EmptyState title="正在读取…" description="" />

  const groups = KIND_ORDER
    .map((k) => ({ kind: k, items: subjects.filter((s) => s.kind === k) }))
    .filter((g) => g.items.length > 0)

  const todo = subjects.filter((s) => s.placeholder).length

  return (
    <div style={{ padding: '4px 16px 32px', display: 'grid', gap: 22 }}>
      {todo > 0 && (
        <div style={{
          padding: '12px 14px', borderRadius: 10, fontSize: 14, lineHeight: 1.6,
          background: 'var(--ps-accent-soft)', color: 'var(--ps-text)',
          border: '1px solid var(--ps-accent-line)',
        }}>
          还有 <strong>{todo}</strong> 项要补：今年报的四门 AP 分别是什么、目标分多少、什么时候考。
          填完这一页才算真正开始。
        </div>
      )}

      {groups.map((g) => (
        <section key={g.kind} style={{ display: 'grid', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 13, letterSpacing: '.08em', color: 'var(--ps-text-dim)' }}>
            {KIND_LABEL[g.kind] ?? g.kind}
          </h2>
          {g.items.map((s) => (
            <SubjectCard
              key={s.id}
              subject={s}
              role={role}
              open={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
              onChanged={load}
            />
          ))}
        </section>
      ))}
    </div>
  )
}

function SubjectCard({ subject: s, role, open, onToggle, onChanged }: {
  subject: Subject; role: 'carson' | 'neo'; open: boolean
  onToggle(): void; onChanged(): void
}) {
  return (
    <div style={{
      border: '1px solid var(--ps-line)', borderRadius: 12, background: 'var(--ps-surface)',
      overflow: 'hidden',
    }}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%', textAlign: 'left', padding: '14px 16px', display: 'grid', gap: 10,
          background: 'transparent', border: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: s.placeholder ? 'var(--ps-text-dim)' : 'var(--ps-text)' }}>
            {s.name}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {s.latestScore ? s.latestScore.score : '—'}
          </span>
          {s.targetScore && (
            <span style={{ fontSize: 13, color: 'var(--ps-text-dim)' }}>/ {s.targetScore}</span>
          )}
        </div>

        {s.ratio !== null ? (
          <Bar ratio={s.ratio} target={s.targetRatio} />
        ) : s.latestScore ? (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ps-text-dim)' }}>
            这门考试的分数区间还没确认，先不画进度条
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 14, fontSize: 12.5, color: 'var(--ps-text-dim)' }}>
          {s.daysToExam !== null && <span>{s.daysToExam > 0 ? `还有 ${s.daysToExam} 天考` : '已考'}</span>}
          {!s.examDate && <span>考期待定</span>}
          {s.logCount > 0 && <span>{s.logCount} 条记录</span>}
          {s.latestScore?.official && <span>已出分</span>}
        </div>
      </button>

      {open && <CardBody subject={s} role={role} onChanged={onChanged} />}
    </div>
  )
}

function Bar({ ratio, target }: { ratio: number; target: number | null }) {
  return (
    <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'var(--ps-line)' }}>
      <div style={{
        position: 'absolute', inset: 0, width: `${ratio * 100}%`,
        borderRadius: 3, background: 'var(--ps-accent)',
      }} />
      {target !== null && (
        <div
          aria-hidden
          style={{
            position: 'absolute', top: -3, height: 12, width: 2, borderRadius: 1,
            left: `calc(${target * 100}% - 1px)`, background: 'var(--ps-text)',
          }}
        />
      )}
    </div>
  )
}

function CardBody({ subject: s, role, onChanged }: {
  subject: Subject; role: 'carson' | 'neo'; onChanged(): void
}) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const canEditInfo = role === 'neo' || s.placeholder

  const [name, setName] = useState(s.placeholder ? '' : s.name)
  const [target, setTarget] = useState(s.targetScore ?? '')
  const [examDate, setExamDate] = useState(s.examDate ?? '')
  const [metric, setMetric] = useState('problems')
  const [value, setValue] = useState('')

  const saveInfo = async () => {
    setBusy(true); setMsg(null)
    const body: Record<string, unknown> = {}
    if (name.trim()) body.name = name.trim()
    if (target.trim()) body.target_score = target.trim()
    if (examDate) body.exam_date = examDate
    const r = await api.patch(`/api/academics/subjects/${s.id}`, body)
    setBusy(false)
    if (r.ok) { setMsg('存好了'); onChanged() } else setMsg(r.message)
  }

  const addLog = async () => {
    if (!value.trim()) return
    setBusy(true); setMsg(null)
    const r = await api.post('/api/academics/logs', {
      subject_id: s.id, metric, value: Number(value),
      unit: metric === 'problems' ? '道' : metric === 'study_minutes' ? '分钟' : null,
    })
    setBusy(false)
    if (r.ok) { setValue(''); setMsg('记下了'); onChanged() } else setMsg(r.message)
  }

  return (
    <div style={{ padding: '0 16px 16px', display: 'grid', gap: 14, borderTop: '1px solid var(--ps-line)' }}>
      {canEditInfo && (
        <div style={{ display: 'grid', gap: 8, paddingTop: 14 }}>
          <Label>{s.placeholder ? '这门课是什么？' : '课程名'}</Label>
          <Field value={name} onChange={setName} placeholder="比如 AP Calculus BC" />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, display: 'grid', gap: 6 }}>
              <Label>目标分</Label>
              <Field value={target} onChange={setTarget} placeholder="5" />
            </div>
            <div style={{ flex: 1.4, display: 'grid', gap: 6 }}>
              <Label>考试日期</Label>
              <Field value={examDate} onChange={setExamDate} type="date" />
            </div>
          </div>
          <Button onClick={saveInfo} disabled={busy}>保存课程信息</Button>
        </div>
      )}

      <div style={{ display: 'grid', gap: 8, paddingTop: canEditInfo ? 0 : 14 }}>
        <Label>记一笔进度</Label>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            aria-label="记录类型"
            style={fieldStyle}
          >
            <option value="problems">刷题</option>
            <option value="study_minutes">学习分钟</option>
            <option value="chapter">章节</option>
            <option value="mock_score">模考</option>
          </select>
          <Field value={value} onChange={setValue} placeholder="数量" type="number" />
        </div>
        <Button onClick={addLog} disabled={busy || !value.trim()}>记下来</Button>
      </div>

      {msg && <p role="status" style={{ margin: 0, fontSize: 13, color: 'var(--ps-text-dim)' }}>{msg}</p>}
    </div>
  )
}

const fieldStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, padding: '10px 12px', fontSize: 16, borderRadius: 9,
  border: '1px solid var(--ps-line)', background: 'var(--ps-bg)', color: 'var(--ps-text)',
}

function Field({ value, onChange, placeholder, type = 'text' }: {
  value: string; onChange(v: string): void; placeholder?: string; type?: string
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={fieldStyle}
    />
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 12.5, color: 'var(--ps-text-dim)' }}>{children}</span>
}
